/**
 * PmsToday — `/pms/today`, la vista previa del futuro Hoy (PRODUCT_V2_SPEC §5).
 *
 *   PmsShell (aviso de demo + barra de pestañas del PMS)
 *     └── cabecera: propiedad, fecha local, resumen del día y vista de rol
 *     └── bloques: atención · resumen · llegadas · salidas · limpieza ·
 *         incidencias · preparación del huésped · peticiones · tablero
 *
 * Reglas de la pantalla:
 *   - Hoy es una VISTA. No guarda pendientes propios: los avisos son una
 *     maqueta de lo que emitirá el Action Center (§5.2, §16.1).
 *   - Hoy enseña el resumen y las excepciones. El detalle completo vive en
 *     Reservas, Housekeeping, Incidencias, Huéspedes y Servicios; aquí se
 *     llega a él con "Ver todo" y con filas que se abren.
 *   - Todo se deriva de los datos de demostración del PMS y de Ajustes V2:
 *     vocabulario, horarios, servicios y requisitos del huésped.
 *   - Ninguna acción llama a la API. El estado vive en `todayV2Store` y se
 *     pierde al recargar.
 *
 * La ruta de producción `/dashboard` y el overview de la demo `/pms` no se
 * tocan: esta pantalla vive aparte.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PmsShell from "@/pages/admin/pms/PmsShell";
import { useMediaQuery, DESKTOP_QUERY } from "@/hooks/useMediaQuery";
import { Check, Info } from "@phosphor-icons/react";

import { useSettingsV2, unitLabelFor } from "@/lib/settingsV2Store";
import { usePmsIssues } from "@/lib/pmsRuntimeStore";
import {
  useTodayV2, setRolePreview, advanceHousekeeping, assignHousekeeping,
  advanceRequest, completeRequest, markArrivalCheckedIn,
} from "@/lib/todayV2Store";
import { useGuestsV2 } from "@/lib/guestsV2Store";
import { useReservationsV2 } from "@/lib/reservationsV2Store";
import { buildTodayV2, rolePreviewFor, attentionForRole } from "@/lib/todayV2Selectors";
import { TODAY_DEMO_NOTE, HOUSEKEEPING_FLOW, HOUSEKEEPING_LABEL } from "@/data/todayV2Demo";

import RolePreview from "@/pages/admin/pms/today/RolePreview";
import NeedsAttention from "@/pages/admin/pms/today/NeedsAttention";
import TodaySnapshot from "@/pages/admin/pms/today/TodaySnapshot";
import ArrivalsBlock from "@/pages/admin/pms/today/ArrivalsBlock";
import DeparturesBlock from "@/pages/admin/pms/today/DeparturesBlock";
import HousekeepingBlock from "@/pages/admin/pms/today/HousekeepingBlock";
import IssuesBlock from "@/pages/admin/pms/today/IssuesBlock";
import GuestReadinessBlock from "@/pages/admin/pms/today/GuestReadinessBlock";
import GuestRequestsBlock from "@/pages/admin/pms/today/GuestRequestsBlock";
import UnitBoard from "@/pages/admin/pms/today/UnitBoard";

/**
 * Orden en móvil: primero lo que hay que resolver, después el trabajo del día
 * y al final el contexto. Es literalmente el orden del DOM, no un truco de
 * CSS, para que la lectura y el foco sigan la misma prioridad.
 */
const MOBILE_ORDER = [
  "attention", "arrivals", "departures", "housekeeping", "issues",
  "readiness", "requests", "board",
];

/** En escritorio el ancho sobrante se usa para tener el contexto al lado. */
const DESKTOP_MAIN = ["arrivals", "departures", "housekeeping", "readiness", "requests"];
const DESKTOP_SIDE = ["board", "issues"];

export default function PmsToday() {
  const desktop = useMediaQuery(DESKTOP_QUERY);
  const settings = useSettingsV2();
  const issues = usePmsIssues();
  const state = useTodayV2();
  const guestState = useGuestsV2();
  const reservationsState = useReservationsV2();

  const unitLabel = useMemo(() => unitLabelFor(settings.property), [settings.property]);
  const today = useMemo(
    () => buildTodayV2({ state, settings, issues, unitLabel, guestState, reservationsState }),
    [state, settings, issues, unitLabel, guestState, reservationsState],
  );

  const role = rolePreviewFor(state.rolePreview);
  const attention = useMemo(
    () => attentionForRole(today.attention, role),
    [today.attention, role],
  );

  const [selectedUnitId, setSelectedUnitId] = useState(null);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  const firstRender = useRef(true);

  const showToast = useCallback((text) => {
    setToast(text);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2400);
  }, []);

  // Cualquier mutación del store avisa igual, sin temporizadores repartidos.
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    showToast("Actualizado en la vista previa");
  }, [state.tick, showToast]);

  useEffect(() => () => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
  }, []);

  const goToSection = useCallback((key) => {
    const el = document.getElementById(`today-${key}`);
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "start", behavior: "smooth" });
    }
  }, []);

  const onMessage = useCallback((arrival) => {
    // Deliberadamente no se envía nada: el canal real (WhatsApp/e-mail) existe
    // en el sistema, pero esta pantalla es una vista previa.
    showToast(`Sin envío en la vista previa · ${arrival.reservation.guest.name}`);
  }, [showToast]);

  const blocks = {
    attention: (
      <NeedsAttention key="attention" items={attention} onGoToSection={goToSection} />
    ),
    arrivals: (
      <ArrivalsBlock
        key="arrivals"
        arrivals={today.arrivals}
        housekeeping={today.housekeeping}
        desktop={desktop}
        showGuestNames={role.guest_names}
        unitLabel={unitLabel}
        onCheckIn={markArrivalCheckedIn}
        onMessage={onMessage}
      />
    ),
    departures: (
      <DeparturesBlock
        key="departures"
        departures={today.departures}
        housekeeping={today.housekeeping}
        showGuestNames={role.guest_names}
        onGoToSection={goToSection}
      />
    ),
    housekeeping: (
      <HousekeepingBlock
        key="housekeeping"
        tasks={today.housekeeping}
        onAdvanceTask={advanceHousekeeping}
        onAssignTask={assignHousekeeping}
        onCompleteRequest={completeRequest}
      />
    ),
    issues: (
      <IssuesBlock key="issues" issues={today.issues} board={today.board} />
    ),
    readiness: (
      <GuestReadinessBlock key="readiness" readiness={today.readiness} />
    ),
    requests: (
      <GuestRequestsBlock
        key="requests"
        requests={today.requests}
        showGuestNames={role.guest_names}
        onAdvanceRequest={advanceRequest}
        onCompleteRequest={completeRequest}
      />
    ),
    board: (
      <UnitBoard
        key="board"
        board={today.board}
        unitLabel={unitLabel}
        selectedUnitId={selectedUnitId}
        onSelectUnit={setSelectedUnitId}
        showGuestNames={role.guest_names}
        columns={2}
      />
    ),
  };

  const visible = (keys) => keys.filter((k) => role.sections.includes(k));

  return (
    <PmsShell>
      <TodayHeader
        propertyName={settings.property.name}
        dateLabel={today.dateLabel}
        snapshot={today.snapshot}
        unitLabel={unitLabel}
        checkInTime={today.checkInTime}
        checkOutTime={today.checkOutTime}
        role={role}
        rolePreview={state.rolePreview}
        onRoleChange={setRolePreview}
      />

      {role.sections.includes("snapshot") && (
        <TodaySnapshot
          snapshot={today.snapshot}
          visibleSections={role.sections}
          onGoToSection={goToSection}
        />
      )}

      {desktop ? (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-x-8" data-testid="today-layout-desktop">
          <div className="lg:col-span-2">
            {visible(["attention"]).map((k) => blocks[k])}
          </div>
          <div className="min-w-0">{visible(DESKTOP_MAIN).map((k) => blocks[k])}</div>
          <div className="min-w-0">{visible(DESKTOP_SIDE).map((k) => blocks[k])}</div>
        </div>
      ) : (
        <div data-testid="today-layout-mobile">
          {visible(MOBILE_ORDER).map((k) => blocks[k])}
        </div>
      )}

      {toast && (
        <div
          role="status"
          data-testid="today-toast"
          className="fixed z-40 bottom-[calc(1rem_+_env(safe-area-inset-bottom,0px))] left-1/2 -translate-x-1/2 lg:left-auto lg:right-6 lg:translate-x-0 inline-flex items-center gap-2 text-xs text-stone-100 dark:text-stone-900 bg-stone-900/95 dark:bg-stone-100/95 rounded-full pl-2.5 pr-3.5 py-1.5 shadow-lg pointer-events-none max-w-[90vw]"
        >
          <Check size={12} weight="bold" className="shrink-0" />
          <span className="truncate">{toast}</span>
        </div>
      )}
    </PmsShell>
  );
}

/* -------------------------------------------------------------------------
 * Cabecera
 * ---------------------------------------------------------------------- */

/**
 * Sin muro de KPIs y sin párrafos: nombre, fecha local y una línea con lo que
 * hay hoy. Todo lo que explica el prototipo vive detrás del icono de
 * información, para que no compita con la información operativa.
 */
function TodayHeader({
  propertyName, dateLabel, snapshot, unitLabel, checkInTime, checkOutTime,
  role, rolePreview, onRoleChange,
}) {
  const [noteOpen, setNoteOpen] = useState(false);

  return (
    <div className="mb-5" data-testid="today-header">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] tracking-[0.3em] uppercase text-stone-500">Hoy</div>
          <h1 className="font-serif-dv text-2xl sm:text-3xl text-stone-900 dark:text-stone-100 truncate">
            {propertyName}
          </h1>
        </div>
        <RolePreview value={rolePreview} onChange={onRoleChange} />
      </div>

      <div className="mt-1 flex items-center gap-2 flex-wrap">
        <p className="text-sm text-stone-600 dark:text-stone-300 first-letter:uppercase" data-testid="today-summary">
          {dateLabel}
          {" · "}
          {snapshot.occupiedTonight} {unitLabel.plural.toLowerCase()} ocupadas
          {" · "}
          {snapshot.arrivals} {snapshot.arrivals === 1 ? "llegada" : "llegadas"}
          {" · "}
          {snapshot.departures} {snapshot.departures === 1 ? "salida" : "salidas"}
        </p>
        <button
          type="button"
          onClick={() => setNoteOpen((v) => !v)}
          aria-expanded={noteOpen}
          aria-label="Qué es esta vista previa"
          title="Qué es esta vista previa"
          data-testid="today-demo-note-toggle"
          className="w-11 h-11 -my-2 flex items-center justify-center text-stone-400 hover:text-stone-700 dark:hover:text-stone-200"
        >
          <Info size={14} />
        </button>
      </div>

      {noteOpen && (
        <div
          className="mt-2 text-[11px] leading-relaxed text-stone-500 dark:text-stone-400 max-w-2xl space-y-1"
          data-testid="today-demo-note"
        >
          <p>{TODAY_DEMO_NOTE}</p>
          <p data-testid="today-times">
            Entrada {checkInTime} · salida {checkOutTime} · según Ajustes, igual que el vocabulario
            y el catálogo de servicios.
          </p>
          <p>
            Limpieza: {HOUSEKEEPING_FLOW.map((s) => HOUSEKEEPING_LABEL[s]).join(" → ")}. El estado
            de cada unidad en el tablero se deduce de esas tareas, no de un interruptor aparte.
          </p>
          <p data-testid="today-privacy-note">
            De los documentos del huésped solo se ve el estado: no se muestran ni se descargan
            desde aquí, y el reporte de inmigración lo presenta la propiedad — no hay integración
            con ningún sistema oficial.
          </p>
          <p data-testid="today-role-summary">
            Vista de rol: {role.summary} Es una maqueta y no cambia permisos.
          </p>
        </div>
      )}
    </div>
  );
}
